import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, firstValueFrom, map, shareReplay, take } from 'rxjs';
import { RequestWrapperClass } from 'src/app/shared/services/request-wrapper.class';
import { Notification, NotificationImportance, NotificationPageGQL, NotificationStatus, PaginationInput, UpdateAllNotificationsGQL, UpdateAllNotificationsInput, UpdateAllNotificationsResult, UpdateNotificationGQL, UpdateNotificationInput, UpdateNotificationResult } from '../generated/schema';
import { UserStateService } from 'src/app/shared/services/user-state.class';
import { sortByDateDesc, sortByUnreadPriority } from 'src/app/shared/consts/notifications';

@Injectable({
    providedIn: 'root',
})
export class NotifcationService extends RequestWrapperClass implements OnDestroy {
    /**
     * Every notification we hold, newest first, from ONE unfiltered request.
     *
     * Deliberately unfiltered: the API applies its filter *after* paginating, so
     * a filtered request returns whichever of the most recent N notifications
     * happen to match rather than the most recent N matches. Slicing an
     * unfiltered newest-first list is correct, so all partitioning by severity
     * and read state happens here on the client.
     *
     * KNOWN LIMIT: this holds at most PAGE_SIZE rows, so `hasUnread$` can only
     * see unread messages inside the newest page. A resident with more than
     * PAGE_SIZE notifications in the API's 30-day window could have unread
     * messages beyond it that the home gate never sees - and `markAllRead`
     * clears those too, since the mutation is server-wide. Closing that needs an
     * unread count from the API (out of scope for VI-500).
     */
    private all$: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
    private loadedCount = 0;

    /** Page size for the one unfiltered query this service uses. */
    private static readonly PAGE_SIZE = 100;

    /**
     * De-duplicates concurrent loads. Home, the archive and the two banner
     * services all call loadNotifications independently, and without this a
     * later-resolving reset can clobber a page that loadMore already appended.
     */
    private inFlightLoad?: Promise<void>;
    private inFlightMore?: Promise<boolean>;

    /**
     * Ids the RESIDENT explicitly set back to unread in this session.
     *
     * Excluded from `criticalNotifications` below, because the banner services
     * treat anything arriving in that stream as a fresh event to act on: one
     * marks a resolved-weather row read again, another re-pops a leak banner.
     * Un-ticking a message in Message History puts it back into the stream, so
     * without this the resident's own action reads as a new event and is undone.
     */
    private residentUnread = new Set<string>();

    constructor(
        private notificationPageService: NotificationPageGQL,
        private updateNotfifcationService: UpdateNotificationGQL,
        private updateAllNotificationService: UpdateAllNotificationsGQL,
        public userStateService: UserStateService,
    ) {
        super(userStateService);
    }

    /** Unread only, in widget order: Critical -> Medium -> Low, newest first. */
    public readonly unread$: Observable<Notification[]> = this.all$.pipe(
        map(list => sortByUnreadPriority(list.filter(n => n.status === NotificationStatus.NotRead))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /** Whether the home widget should exist at all. */
    public readonly hasUnread$: Observable<boolean> = this.all$.pipe(
        map(list => list.some(n => n.status === NotificationStatus.NotRead)),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * Read only, newest first. The archive shows two categories - unread above
     * read - so it reads these two streams rather than one merged list: unread
     * is ordered by severity, read purely by date.
     */
    public readonly read$: Observable<Notification[]> = this.all$.pipe(
        map(list => sortByDateDesc(list.filter(n => n.status === NotificationStatus.Read))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * Unread criticals only, newest first. The water-leak and severe-weather
     * banners watch this to drive their own UI.
     *
     * `distinctUntilChanged` on the id list is load-bearing, not an optimisation:
     * those services call showAlert() unconditionally on every emission and
     * dismiss only their own local state, so a bare re-emission of an unchanged
     * critical set makes a banner the resident already dismissed pop straight
     * back up. Only emit when the set of unread criticals actually changes.
     */
    public readonly criticalNotifications: Observable<Notification[]> = this.all$.pipe(
        map(list => sortByDateDesc(list.filter(
            n => n.status === NotificationStatus.NotRead
                && n.importance === NotificationImportance.Critical
                && !this.residentUnread.has(n.id),
        ))),
        distinctUntilChanged((a, b) => a.length === b.length && a.every((n, i) => n.id === b[i].id)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    /**
     * Goes through createRetryableObserver like every other call in this app, so
     * a dropped connection parks and resumes on reconnect instead of silently
     * leaving the list empty for the rest of the session.
     */
    /**
     * Resolves null when the server returned no data, and [] only when it really
     * returned an empty list.
     *
     * The distinction is load-bearing. Apollo is configured with
     * `query: { errorPolicy: 'all' }` (graphql.module.ts), so a GraphQL error
     * RESOLVES with `data: null` instead of throwing - a catch block never sees
     * it. Collapsing that to [] would blank all$, and because hasUnread$ gates
     * the whole Messages widget the resident would see it vanish behind a false
     * "All Caught Up" and an empty archive.
     *
     * Deliberately NOT wrapped in createRetryableObserver. That helper parks the
     * stream on `retryCalls$` for network errors and waits to be released by
     * retry() - which NOTHING in src ever calls. The promise would then never
     * settle, so `inFlightLoad` below would never clear and every later load
     * would return the same dead promise: one offline moment and Messages is
     * gone for the rest of the process. A plain rejection is recoverable.
     */
    private async fetchPage(skip: number): Promise<Notification[] | null> {
        const variables: { pagination: PaginationInput } = {
            pagination: { limit: NotifcationService.PAGE_SIZE, skip },
        };

        const response = await this.notificationPageService.fetch(variables).toPromise();
        const notifications = response?.data?.notifications;
        return notifications ? <Notification[]>notifications : null;
    }

    /**
     * Refreshes the newest page.
     *
     * Replaces the list outright in the ordinary case, so rows read on another
     * device - or a mark-all, or an expiry - actually disappear. The exception is
     * a resident who has paged deeper: home calls this on every app resume while
     * Message History sits on its own stack above it, and replacing there would
     * collapse their archive back to 100 rows and jump their scroll position. So
     * when deeper pages are loaded AND the server still returns a full first page,
     * everything from page two onward is kept behind the refreshed page.
     *
     * A short page means the whole archive now fits in one page, so there is no
     * deeper tail to keep and the replace path is the correct one.
     */
    public loadNotifications(): Promise<void> {
        if (this.inFlightLoad) return this.inFlightLoad;

        this.inFlightLoad = this.fetchPage(0)
            .then(items => {
                // Keep the last good list on failure rather than blanking the UI.
                if (items === null) {
                    console.error('loadNotifications: no data returned, keeping the current list');
                    return;
                }
                const pageSize = NotifcationService.PAGE_SIZE;
                if (items.length < pageSize || this.loadedCount <= pageSize) {
                    this.loadedCount = items.length;
                    this.all$.next(items);
                    return;
                }

                // Only the rows past the first page, and only those the refreshed
                // page does not already carry - so a row that genuinely left page
                // one is not resurrected from the tail.
                const refreshed = new Set(items.map(n => n.id));
                const deeper = this.all$.value.slice(pageSize).filter(n => !refreshed.has(n.id));
                // loadedCount stays put: those deeper pages are still loaded.
                this.all$.next([...items, ...deeper]);
            })
            .catch(error => {
                // Reachable now that fetchPage is not parked by
                // createRetryableObserver: swallow so the guard below clears and
                // the next call - resume, pull-to-refresh - can try again.
                console.error('loadNotifications failed', error);
            })
            .finally(() => {
                this.inFlightLoad = undefined;
            });

        return this.inFlightLoad;
    }

    /**
     * Alias kept for the water-leak and severe-weather banners, which call this
     * on their own init so they do not depend on the home widget being mounted.
     * Safe to call repeatedly - concurrent calls share one request.
     */
    public initNotifications(): Promise<void> {
        return this.loadNotifications();
    }

    /**
     * Appends the next page.
     *
     * Resolves FALSE only when the API confirmed there is nothing more, and
     * THROWS when the fetch failed. Callers use the false to stop asking, so
     * conflating the two would let one transient failure permanently retire the
     * archive's infinite scroll.
     */
    public loadMore(): Promise<boolean> {
        // Its own guard, not just a wait on inFlightLoad: two overlapping
        // loadMore calls would both read the same loadedCount, both fetch the
        // same page, and both advance the counter - skipping a page of history.
        if (this.inFlightMore) return this.inFlightMore;

        this.inFlightMore = this.appendNextPage()
            .finally(() => { this.inFlightMore = undefined; });

        return this.inFlightMore;
    }

    private async appendNextPage(): Promise<boolean> {
        // Never append onto a list that is about to be replaced.
        if (this.inFlightLoad) await this.inFlightLoad;

        const skip = this.loadedCount;
        const items = await this.fetchPage(skip);
        if (items === null) throw new Error('loadMore: no data returned');
        if (!items.length) return false;

        // A reset landed while this page was in flight, so its contents are
        // already stale. Report true - there may well be more - so the caller
        // does not mark the list exhausted on what is really a race.
        if (skip !== this.loadedCount) return true;

        const known = new Set(this.all$.value.map(n => n.id));
        const fresh = items.filter(n => !known.has(n.id));

        this.loadedCount += items.length;
        if (fresh.length) this.all$.next([...this.all$.value, ...fresh]);
        return true;
    }

    /**
     * Marks every message read. The mutation takes only a status - no severity
     * and no id list - so this reaches messages beyond the loaded page too.
     * Resolves the number of loaded messages that were unread.
     */
    public async markAllRead(): Promise<number> {
        const clearedIds = this.all$.value
            .filter(n => n.status === NotificationStatus.NotRead)
            .map(n => n.id);

        // Which of these the resident had deliberately un-read, so a failed
        // mutation can put those marks back as well.
        const clearedMarks = clearedIds.filter(id => this.residentUnread.has(id));

        this.all$.next(this.all$.value.map(n => ({ ...n, status: NotificationStatus.Read })));
        clearedIds.forEach(id => this.residentUnread.delete(id));

        const revert = () => {
            // Re-mark by ID against the CURRENT list rather than replaying a
            // pre-await snapshot: a load or a banner write may have landed while
            // the mutation was in flight, and replaying would discard it.
            const stale = new Set(clearedIds);
            this.all$.next(this.all$.value.map(
                n => (stale.has(n.id) ? { ...n, status: NotificationStatus.NotRead } : n),
            ));
            // Restoring status alone is not enough: without the un-read marks,
            // criticalNotifications hands these rows back to the banner services,
            // which mark them read again - undoing the deliberate un-read this
            // set exists to protect.
            clearedMarks.forEach(id => this.residentUnread.add(id));
        };

        try {
            const ok = await firstValueFrom(this.updateAllNotfifcation(NotificationStatus.Read));
            if (!ok) {
                revert();
                return 0;
            }
        } catch (error) {
            console.error('markAllRead failed', error);
            revert();
            return 0;
        }
        // Only report a count the server actually accepted.
        return clearedIds.length;
    }

    /**
     * Flips one message's read status optimistically and rolls back if the write
     * is rejected. Deliberately does NOT unsubscribe early: the retryable
     * observer needs to stay subscribed to resume after a reconnect.
     */
    public setRead(notification: Notification, status: NotificationStatus): void {
        const previous = notification.status;

        // Remember a deliberate un-read so the banner services do not treat it
        // as a new event and undo it. Re-reading clears the mark.
        if (status === NotificationStatus.NotRead) this.residentUnread.add(notification.id);
        else this.residentUnread.delete(notification.id);

        this.applyReadStatus(notification.id, status);

        this.setReadStatus(notification, status).pipe(take(1)).subscribe({
            next: ok => {
                if (!ok) this.applyReadStatus(notification.id, previous);
            },
            error: error => {
                console.error('setRead failed', error);
                this.applyReadStatus(notification.id, previous);
            },
        });
    }

    /** Applies a read-status change to the in-memory list without a refetch. */
    public applyReadStatus(id: string, status: NotificationStatus): void {
        this.all$.next(this.all$.value.map(n => (n.id === id ? { ...n, status } : n)));
    }

    public setReadStatus(notification: Notification, status: NotificationStatus): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            const variables: { input: UpdateNotificationInput } = {
                input: {
                    id: notification.id,
                    status: status
                }
            };

            const response = await this.updateNotfifcationService.mutate(variables).toPromise();
            return response?.data?.updateNotification == UpdateNotificationResult.NotificationUpdated;
        });
    }

    /**
     * Used by the water-leak and severe-weather banners. Mirrors the result into
     * `all$` on success: without that, a banner marking its notification read
     * left the client showing it as unread while the server disagreed, and the
     * two only reconciled on the next full load.
     */
    updateNotfifcation(id: string, status: NotificationStatus): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {

            const variables: { input: UpdateNotificationInput } = {
                input: {
                    id: id,
                    status: status
                }
            };

            const response = await this.updateNotfifcationService.mutate(variables).toPromise();
            const updated = response?.data?.updateNotification == UpdateNotificationResult.NotificationUpdated;

            if (updated) this.applyReadStatus(id, status);
            return updated;
        });
    }

    updateAllNotfifcation(status: NotificationStatus): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            const variables: { input: UpdateAllNotificationsInput } = {
                input: {
                    status: status
                }
            };

            const response = await this.updateAllNotificationService.mutate(variables).toPromise();
            return response?.data?.updateAllNotifications == UpdateAllNotificationsResult.AllNotificationsUpdated ? true : false;
        });
    }

    ngOnDestroy(): void {
        this.all$.complete();
    }
}
